/**
 * Created by jonas on 2021-02-09.
 */
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {SimpleReelModel, SimpleReelModelSymbolData} from "../../core/reel/simplereel/SimpleReelModel";
import {gsap} from "gsap";
import {DasUBootReel} from "./DasUBootReel";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {ReelFrame} from "../view/parts/ReelFrame";
import {PeriscopeView} from "../view/parts/PeriscopeView";
import {DasUBootStatics} from "../utils/DasUBootStatics";
import {DasUBoot} from "../DasUBoot";
import {DasUBootSymbol, DasUBootSymbolStates} from "./DasUBootSymbol";
import {DasUBootSound} from "../sound/DasUBootSound";

export enum DasUBootReelsViewState {
    NORMAL,
    TORPEDO,
    PERISCOPE
}

export class DasUBootReelsView extends PIXI.Container implements StageLayer {
    private _state:DasUBootReelsViewState = DasUBootReelsViewState.NORMAL;
    private overlay:PIXI.Sprite;
    private areaResizeAnimation:gsap.core.Tween | undefined;


    get reels():DasUBootReel[] {
        return this._reels;
    }

    get reelModels():SimpleReelModel[] {
        return this._reelModels;
    }

    public reelFrame:ReelFrame;

    public periscopeView:PeriscopeView;

    private _reels:DasUBootReel[] = [];
    private _mainContainer:PIXI.Container;
    private _reelContainerWin:PIXI.Container;
    private _reelContainerNormal:PIXI.Container;

    private _reelModels:SimpleReelModel[];

    constructor() {
        super();
        this.init();
    }

    private init() {

        this.reelFrame = new ReelFrame();

        this.periscopeView = new PeriscopeView();
        this.periscopeView.view.position.set(DasUBootStatics.symbolWidth * 3, 440);

        this._mainContainer = new PIXI.Container();
        this._mainContainer.name = "_mainContainer";

        this._reelContainerNormal = new PIXI.Container();
        this._reelContainerNormal.name = "_reelContainerNormal";

        this._reelContainerWin = new PIXI.Container();
        this._reelContainerWin.name = "_reelContainerWin";


        this.overlay = new PIXI.Sprite(PIXI.Texture.WHITE);
        this.overlay.anchor.set(0.5, 0.5);
        this.overlay.position.set(360, 360);
        this.overlay.alpha = 0;
        this.overlay.blendMode = PIXI.BLEND_MODES.MULTIPLY;

        this._mainContainer.addChild(this.reelFrame.behindNormalReelsContainer);
        this._mainContainer.addChild(this._reelContainerNormal);
        this._mainContainer.addChild(this.reelFrame.overNormalReelsContainer);
        this._mainContainer.addChild(this.overlay);
        this._mainContainer.addChild(this._reelContainerWin);
        this._mainContainer.addChild(this.reelFrame.overWinReelsContainer);
        this._mainContainer.addChild(this.periscopeView.view);


        this._reelModels = [];
        for (let i:number = 0; i < DasUBootStatics.defaultGameAreaSize.length; i++) {
            const reel = new DasUBootReel(i);
            this._reelModels.push(reel.model);
            this._reels.push(reel);
            this._reelContainerNormal.addChild(reel);

        }
        for (let reel of this._reels) {
            this._reelContainerWin.addChild(reel.winLayer);
        }

        for (let symbol of this.getAllVisibleSymbols()) {
            (<DasUBootSymbol>symbol.positionable).getStateAnimation(DasUBootSymbolStates.NORMAL);
        }


        this.addChild(this._mainContainer);

        this.resizeArea(0);

    }


    private getReelAreaHeight():number {
        const extra = 60;
        switch (this._state) {
            case DasUBootReelsViewState.TORPEDO:
                return DasUBootStatics.symbolHeight * 5 + extra;
            case DasUBootReelsViewState.PERISCOPE:
                return DasUBootStatics.symbolHeight * 6 + extra;
            default:
                return DasUBootStatics.symbolHeight * 4 + extra;

        }
    }

    private getReelAreaWidth():number {
        return DasUBootStatics.symbolWidth * 6;
    }


    private updateOverlay(mainContainerScale:number, position:PIXI.Point) {
        this.overlay.width = NolimitApplication.screenBounds.width;
        this.overlay.height = NolimitApplication.screenBounds.height;
        this.overlay.position.set(
            position.x,
            position.y
        );
        this.overlay.scale.x = this.overlay.scale.x * (1 / mainContainerScale);
        this.overlay.scale.y = this.overlay.scale.y * (1 / mainContainerScale);
    }

    onResize():void {
        this.resizeArea(0);
    }


    public getVisibleSymbolAt(column:number, row:number):SimpleReelModelSymbolData {
        return this._reelModels[column].getVisibleSymbolAt(row);
    }

    public getVisibleSymbolInReel(column:number):SimpleReelModelSymbolData[] {
        return this._reelModels[column].getAllVisibleSymbols();
    }

    public getAllVisibleSymbols():SimpleReelModelSymbolData[] {
        const visibleSymbols:SimpleReelModelSymbolData[] = [];
        for (let model of this._reelModels) {
            for (let symbol of model.getAllVisibleSymbols()) {
                visibleSymbols.push(symbol);
            }
        }
        return visibleSymbols;
    }

    public getAllSymbols():SimpleReelModelSymbolData[] {
        let allSymbols:SimpleReelModelSymbolData[] = [];
        for (let model of this._reelModels) {
            allSymbols = allSymbols.concat(model.symbols);
        }
        return allSymbols;
    }

    public getVisibleSymbolsWhere(callback:(value:SimpleReelModelSymbolData) => boolean):SimpleReelModelSymbolData[] {
        const visibleSymbols:SimpleReelModelSymbolData[] = [];
        for (let model of this._reelModels) {
            for (let symbol of model.getAllVisibleSymbols()) {
                if (callback(symbol)) {
                    visibleSymbols.push(symbol);
                }
            }
        }
        return visibleSymbols;
    }

    public raisePeriscope(raise:boolean, duration:number = 0.5, sound:boolean = true):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        duration = duration == 0 ? 0.0001 : duration;

        if (raise) {
            tl.add([
                DasUBoot.reelView.getAnimationTimeResize(duration, DasUBootReelsViewState.PERISCOPE),
                gsap.to(this._reelContainerNormal, {duration:duration, pixi:{y:DasUBootStatics.symbolHeight * 2}}),
                gsap.to(this._reelContainerWin, {duration:duration, pixi:{y:DasUBootStatics.symbolHeight * 2}}),
                gsap.to(this.reelFrame.position, {duration:duration, y:DasUBootStatics.symbolHeight * 2}),
            ]);
            if (sound) {
                tl.add(() => DasUBoot.soundPlayer.playSound(DasUBootSound.RAISE_PERISCOPE));
            }
            tl.add([
                this._reels[2].growReel(true, duration * 4),
                this.reelFrame.openPeriscope(duration * 4)
            ]);

        } else {
            if (sound) {
                tl.add(() => DasUBoot.soundPlayer.playSound(DasUBootSound.RAISE_PERISCOPE));
            }
            tl.add([
                this._reels[2].growReel(false, duration * 4),
                this.reelFrame.closePeriscope(duration * 4)
            ]);
            tl.add([
                //this._reels[3].growReel(false),
                this.getAnimationTimeResize(duration, DasUBootReelsViewState.NORMAL),
                gsap.to(this._reelContainerNormal, {duration:duration, pixi:{y:0}}),
                gsap.to(this._reelContainerWin, {duration:duration, pixi:{y:0}}),
                gsap.to(this.reelFrame.position, {duration:duration, y:0}),
            ]);


        }

        return tl;
    }

    public getAnimationTimeResize(duration:number, state:DasUBootReelsViewState = DasUBootReelsViewState.NORMAL):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {this._state = state});
        tl.add(() => {this.resizeArea(duration)});
        tl.add(gsap.to(this, {duration:duration}));
        return tl;
    }

    private resizeArea(duration:number = 0):gsap.core.Tween {

        if (this.areaResizeAnimation && this.areaResizeAnimation.isActive()) {
            duration = this.areaResizeAnimation.duration() * (1 - this.areaResizeAnimation.progress());
            this.areaResizeAnimation.kill();
        }

        const height = this.getReelAreaHeight();
        const width = this.getReelAreaWidth();

        const margin = new PIXI.Point(
            NolimitApplication.isLandscape ? 200 : 0,
            NolimitApplication.isLandscape ? 80 : 144
        );

        const scale = Math.min(1, Math.min(
            (NolimitApplication.screenBounds.width - margin.x) / width,
            (NolimitApplication.screenBounds.height - margin.y) / height,
        ));

        const position = new PIXI.Point(
            360 - width * scale * 0.5,
            360 - height * scale * 0.5,
        )

        if (NolimitApplication.isLandscape) {
            //position.y -= margin.y * 0.5;
        } else {
            //Makes reel area to stay above keypad on shorter aspect ratios closer to 1.
            position.y = position.y - (280 + NolimitApplication.screenBounds.top) / 2;
        }

        this.updateOverlay(scale, new PIXI.Point(width * 0.5, height * 0.5));
        this.periscopeView.resizeBackground(scale, new PIXI.Point(width * 0.5, height * 0.5));

        this.areaResizeAnimation = gsap.to(this._mainContainer, {
            duration:duration, pixi:{scale:scale, x:position.x, y:position.y}, onComplete:() => {
                delete this.areaResizeAnimation;
            }
        });

        return this.areaResizeAnimation;

    }

    public bombShake(duration:number = 0.5):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(gsap.to(this.position, {
            duration:duration,
            x:"-=5",
            ease:DasUBootStatics.bombShakeWiggleX
        }));

        tl.add(gsap.to(this.position, {
            duration:duration,
            y:"-=10",
            ease:DasUBootStatics.bombShakeWiggleY
        }), 0);
        return tl;
    }

    public getLandingShake(reverse:boolean):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();

        if (reverse) {
            tl.add(gsap.to(this.position, {duration:0.1, y:-10, ease:DasUBootStatics.reelLandingShake}), 0);
        } else {
            tl.add(gsap.to(this.position, {duration:0.1, y:10, ease:DasUBootStatics.reelLandingShake}), 0);
        }

        return tl;
    }


    public reInitReels(reels:string[][]) {
        DasUBoot.model.resetVisibleReels(reels);
        for (let reelModel of this.reelModels) {
            reelModel.init(reels[reelModel.id]);
        }
        for (let symbol of this.getAllVisibleSymbols()) {
            (<DasUBootSymbol>symbol.positionable).getStateAnimation(DasUBootSymbolStates.NORMAL);
        }
    }

    public startPeriscopeNearWin(periscopeNearWinTint:number):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();

        tl.add(() => {
            this._reelContainerWin.addChild(this._reels[2])
            this.overlay.tint = periscopeNearWinTint;
        });
        tl.add(gsap.to(this.overlay, {duration:0.5, alpha:0.8}));

        for (let i:number = 0; i < this.reelFrame.reelLamps.length; i++) {
            if (i == 2) {
                continue;
            }
            tl.add(gsap.to(this.reelFrame.reelLamps[i], {duration:0.5, pixi:{tint:periscopeNearWinTint}}), "<");
        }


        return tl;
    }

    public stopPeriscopeNearWin():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {this._reelContainerNormal.addChild(this._reels[2])});
        tl.add(gsap.to(this.overlay, {duration:0.5, alpha:0.0}));

        for (let i:number = 0; i < this.reelFrame.reelLamps.length; i++) {
            if (i == 2) {
                continue;
            }
            tl.add(gsap.to(this.reelFrame.reelLamps[i], {duration:0.5, pixi:{tint:0xffffff}}), "<");
        }

        return tl;
    }

}